import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  overview: {
    users: {
      total: number;
      newThisMonth: number;
      byRole: Record<string, number>;
    };
    programs: {
      total: number;
      active: number;
      participants: number;
    };
    jobs: {
      total: number;
      active: number;
      applications: number;
    };
    donations: {
      total: number;
      totalAmount: number;
      avgDonation: number;
      recentCount: number;
      recentAmount: number;
    };
    contacts: {
      byStatus: Record<string, number>;
      newContacts: number;
    };
  };
  trends: {
    userGrowth: Array<{_id: {year: number, month: number}, count: number}>;
    donationGrowth: Array<{_id: {year: number, month: number}, count: number, amount: number}>;
  };
}

interface RecentActivities {
  users: Array<{_id: string, name: string, email: string, role: string, createdAt: string}>;
  contacts: Array<{_id: string, name: string, email: string, subject: string, status: string, priority: string, createdAt: string}>;
  volunteers: Array<{_id: string, fullName: string, email: string, phone: string, city: string, createdAt: string}>;
  donations: Array<{_id: string, amount: number, currency: string, purpose: string, donatedAt: string, donor: {name: string}}>;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<RecentActivities | null>(null);
  const [volunteers, setVolunteers] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const getAuthToken = () => localStorage.getItem('token');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      if (!token) {
        setError('Please login to access admin dashboard');
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch dashboard stats
      const [dashboardRes, activitiesRes, volunteersRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/admin/dashboard`, { headers }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/admin/recent-activities`, { headers }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/volunteers`, { headers })
      ]);

      if (dashboardRes.ok) {
        const dashboardData = await dashboardRes.json();
        setStats(dashboardData.data);
      }

      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData.data);
      }

      if (volunteersRes.ok) {
        const volunteersData = await volunteersRes.json();
        setVolunteers(volunteersData.data || []);
      }

    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/login" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Yakjeti Pakistan Welfare Society</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-gray-900">
                Back to Website
              </Link>
              <button 
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/login';
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="text-3xl text-blue-600 mr-4">👥</div>
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold">{stats?.overview.users.total || 0}</p>
                <p className="text-xs text-green-600">+{stats?.overview.users.newThisMonth || 0} this month</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="text-3xl text-green-600 mr-4">📞</div>
              <div>
                <p className="text-sm text-gray-600">Contact Messages</p>
                <p className="text-2xl font-bold">
                  {Object.values(stats?.overview.contacts.byStatus || {}).reduce((a, b) => a + b, 0)}
                </p>
                <p className="text-xs text-orange-600">{stats?.overview.contacts.newContacts || 0} new</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="text-3xl text-purple-600 mr-4">🤝</div>
              <div>
                <p className="text-sm text-gray-600">Volunteers</p>
                <p className="text-2xl font-bold">{volunteers.length}</p>
                <p className="text-xs text-blue-600">Active applications</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="text-3xl text-yellow-600 mr-4">💰</div>
              <div>
                <p className="text-sm text-gray-600">Total Donations</p>
                <p className="text-2xl font-bold">₨{(stats?.overview.donations.totalAmount || 0).toLocaleString()}</p>
                <p className="text-xs text-green-600">{stats?.overview.donations.total || 0} donations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Link to="/admin/contacts" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="text-4xl text-green-600 mr-4">📧</div>
              <div>
                <h3 className="text-lg font-semibold">Manage Contacts</h3>
                <p className="text-gray-600">View and respond to messages</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-green-600">→</span>
            </div>
          </Link>

          <Link to="/admin/volunteers" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="text-4xl text-purple-600 mr-4">🤝</div>
              <div>
                <h3 className="text-lg font-semibold">Manage Volunteers</h3>
                <p className="text-gray-600">Review volunteer applications</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-purple-600">→</span>
            </div>
          </Link>

          <Link to="/admin/users" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="text-4xl text-blue-600 mr-4">👥</div>
              <div>
                <h3 className="text-lg font-semibold">Manage Users</h3>
                <p className="text-gray-600">User accounts and roles</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-blue-600">→</span>
            </div>
          </Link>

          <Link to="/admin/donations" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="text-4xl text-yellow-600 mr-4">💰</div>
              <div>
                <h3 className="text-lg font-semibold">Manage Donations</h3>
                <p className="text-gray-600">Track donation activities</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-yellow-600">→</span>
            </div>
          </Link>

          <Link to="/admin/programs" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="text-4xl text-indigo-600 mr-4">📚</div>
              <div>
                <h3 className="text-lg font-semibold">Manage Programs</h3>
                <p className="text-gray-600">Oversee welfare programs</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-indigo-600">→</span>
            </div>
          </Link>

          <Link to="/admin/jobs" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="text-4xl text-red-600 mr-4">💼</div>
              <div>
                <h3 className="text-lg font-semibold">Manage Jobs</h3>
                <p className="text-gray-600">Job postings and applications</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-red-600">→</span>
            </div>
          </Link>
        </div>

        {/* Recent Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Contacts */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Contact Messages</h3>
            <div className="space-y-3">
              {activities?.contacts?.slice(0, 5).map((contact) => (
                <div key={contact._id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <p className="font-medium">{contact.name}</p>
                    <p className="text-sm text-gray-600">{contact.subject}</p>
                    <p className="text-xs text-gray-500">{new Date(contact.createdAt).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    contact.status === 'new' ? 'bg-orange-100 text-orange-800' :
                    contact.status === 'resolved' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {contact.status}
                  </span>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-4">No recent contacts</p>
              )}
            </div>
            <Link to="/admin/contacts" className="block text-center mt-4 text-green-600 hover:text-green-700">
              View All Contacts →
            </Link>
          </div>

          {/* Recent Volunteers */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Volunteer Applications</h3>
            <div className="space-y-3">
              {volunteers.slice(0, 5).map((volunteer) => (
                <div key={volunteer._id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <p className="font-medium">{volunteer.fullName}</p>
                    <p className="text-sm text-gray-600">{volunteer.email}</p>
                    <p className="text-xs text-gray-500">{volunteer.city} • {new Date(volunteer.createdAt).toLocaleDateString()}</p>
                  </div>
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                    {volunteer.status || 'pending'}
                  </span>
                </div>
              )) || (
                <p className="text-gray-500 text-center py-4">No recent volunteers</p>
              )}
            </div>
            <Link to="/admin/volunteers" className="block text-center mt-4 text-purple-600 hover:text-purple-700">
              View All Volunteers →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
